import React, { useEffect, useMemo, useState } from 'react';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Button, Paper } from '@mui/material';

import { PropertiesPanel } from '@paragon/layout-models';
import { InlineNotification } from '@paragon/notification-tools';
import { createScrollbarStyling } from '@paragon/themes';

import { useAppMonitor } from '../../../monitor';
import { loadMissingOptimizerPrices, optimizeActiveComponentDesign } from '../../../store/actions/optimizationActions';
import { getActiveComponentDesign } from '../../../store/selectors/getActiveComponentDesignSelector';
import {
  getMissingLumberPriceRowsForActiveComponent,
  getMissingPlatePriceRowsForActiveComponent,
} from '../../../store/selectors/missingLumberPriceSelectors';
import { isOptimizationRunningForActiveComponent } from '../../../store/selectors/optimizationProgressSelectors';
import { inViewOnlyMode } from '../../../store/selectors/viewOnlyModeSelectors';
import { setOptimizationPanelClosed } from '../../../store/slices/panelDataSlice';
import { RunWithMissingPricesDialog } from '../../dialogs/RunWithMissingPricesDialog';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { PropertiesSection } from '../subpanels/properties/PropertiesSection';
import { CostComparisonSection } from './optimization/CostComparisonSection';
import { CostFactorsSection } from './optimization/CostFactorsSection';
import { getUnpricedMaterialsWithoutDefaults } from './optimization/missingDefaultPriceGaps';
import { OptimizationProgressSection } from './optimization/OptimizationProgressSection';
import { OptimizerLeversSection } from './optimization/OptimizerLeversSection';

type SectionKey = 'costFactors' | 'levers' | 'comparison';

export function OptimizationPanel() {
  const dispatch = useAppDispatch();
  const monitor = useAppMonitor();

  const activeComponentDesign = useAppSelector(getActiveComponentDesign);
  const isViewOnly = useAppSelector(inViewOnlyMode);
  const lumberRows = useAppSelector(getMissingLumberPriceRowsForActiveComponent);
  const plateRows = useAppSelector(getMissingPlatePriceRowsForActiveComponent);

  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    costFactors: false,
    levers: false,
    comparison: false,
  });
  const [confirmRunOpen, setConfirmRunOpen] = useState(false);

  const activeComponentDesignGuid = activeComponentDesign?.guid;
  useEffect(() => {
    if (activeComponentDesignGuid) void dispatch(loadMissingOptimizerPrices());
  }, [dispatch, activeComponentDesignGuid]);

  const optimizationIsRunning = useAppSelector(isOptimizationRunningForActiveComponent);
  const canOptimize = activeComponentDesign != null && !isViewOnly && !optimizationIsRunning;

  const unpricedMaterials = useMemo(
    () => getUnpricedMaterialsWithoutDefaults(lumberRows, plateRows),
    [lumberRows, plateRows],
  );

  const runOptimization = () => {
    monitor.recordInteraction('Optimization panel: ran optimization');
    void dispatch(optimizeActiveComponentDesign());
  };

  const toggleSection = (section: SectionKey) =>
    setExpandedSections((current) => ({ ...current, [section]: !current[section] }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={[{ flex: 1, minHeight: 0, overflowY: 'auto' }, createScrollbarStyling]}
        className="clickable-during-picking"
      >
        <PropertiesPanel title="Truss Price Optimization">
          <OptimizationProgressSection />

          <InlineNotification type="warning">
            Single-component optimization is in alpha &mdash; runs may take a while, and some functionality is still in
            development.
          </InlineNotification>

          <InlineNotification type="info">
            Enabled cost factors steer which design the optimizer picks. Lumber and plates use your organization&apos;s
            prices, with Paragon&apos;s defaults filling most gaps. Where a lumber has no default, enter a price so the
            optimizer can use it.
          </InlineNotification>

          <PropertiesSection
            title="Cost Factors"
            icon={<AttachMoneyIcon fontSize="small" />}
            expanded={expandedSections.costFactors}
            onExpand={() => toggleSection('costFactors')}
            onCollapse={() => toggleSection('costFactors')}
          >
            <CostFactorsSection disabled={isViewOnly} />
          </PropertiesSection>

          <PropertiesSection
            title="Algorithm Settings"
            icon={<SettingsIcon fontSize="small" />}
            expanded={expandedSections.levers}
            onExpand={() => toggleSection('levers')}
            onCollapse={() => toggleSection('levers')}
          >
            <OptimizerLeversSection disabled={isViewOnly} />
          </PropertiesSection>

          <PropertiesSection
            title="Cost Comparison"
            icon={<RequestQuoteIcon fontSize="small" />}
            expanded={expandedSections.comparison}
            onExpand={() => toggleSection('comparison')}
            onCollapse={() => toggleSection('comparison')}
          >
            <CostComparisonSection />
          </PropertiesSection>
        </PropertiesPanel>
      </Box>

      <Paper elevation={1} sx={{ display: 'flex', justifyContent: 'flex-end', padding: 1 }}>
        <Button sx={{ m: 1 }} variant="outlined" onClick={() => void dispatch(setOptimizationPanelClosed())}>
          Close
        </Button>

        <Button
          sx={{ m: 1 }}
          variant="contained"
          disabled={!canOptimize}
          onClick={() => {
            if (unpricedMaterials.length > 0) {
              setConfirmRunOpen(true);
            } else {
              runOptimization();
            }
          }}
        >
          {optimizationIsRunning ? 'Optimizing...' : 'Run Optimization'}
        </Button>
      </Paper>

      {confirmRunOpen && (
        <RunWithMissingPricesDialog
          materials={unpricedMaterials}
          onClose={(runAnyway) => {
            setConfirmRunOpen(false);
            if (runAnyway) runOptimization();
          }}
        />
      )}
    </Box>
  );
}
